import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'pl', label: 'Polski' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);

  const currentLang = (i18n.language || 'en').split('-')[0];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMobileOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 bg-white border-b ${
        scrolled ? 'border-background-300 shadow-sm' : 'border-background-200'
      }`}
    >
      <nav className="flex items-center justify-between px-4 md:px-6 h-16">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer">
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500 text-white">
            <i className="ri-table-2 text-base" />
          </span>
          <span className="font-heading font-bold text-foreground-950 text-lg">USFansSheet</span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className="text-foreground-800 hover:text-primary-700 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
          >
            {t('nav.home')}
          </Link>
          <Link
            to="/article/usfans-spreadsheet-guide"
            className="text-foreground-800 hover:text-primary-700 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
          >
            {t('nav.shoppingGuide')}
          </Link>
          <a
            href="#categories"
            className="text-foreground-800 hover:text-primary-700 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
          >
            {t('nav.categories')}
          </a>
          <a
            href="#faq"
            className="text-foreground-800 hover:text-primary-700 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
          >
            FAQ
          </a>

          <div className="relative">
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800 hover:text-foreground-950 whitespace-nowrap cursor-pointer px-3 py-1.5 rounded-full border border-background-300"
            >
              <i className="ri-global-line text-sm" />
              {LANGUAGES.find((l) => l.code === currentLang)?.label || 'English'}
              <i className="ri-arrow-down-s-line text-sm" />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 rounded-lg border border-background-200 bg-white py-1.5 z-50">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => changeLang(l.code)}
                    className={`w-full text-left px-4 py-2 text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      currentLang === l.code
                        ? 'text-primary-700 font-semibold bg-primary-100'
                        : 'text-foreground-700 hover:bg-background-100'
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            {t('nav.buyNow')}
          </a>
        </div>

        <button
          type="button"
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-900 cursor-pointer"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
        >
          <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </nav>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-white px-4 py-4 flex flex-col gap-2 max-h-[75vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 text-foreground-900 text-sm font-semibold py-2.5 cursor-pointer"
          >
            <i className="ri-home-4-line text-primary-600" />
            {t('nav.home')}
          </Link>
          <Link
            to="/article/usfans-spreadsheet-guide"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 text-foreground-900 text-sm font-semibold py-2.5 cursor-pointer"
          >
            <i className="ri-shopping-bag-3-line text-primary-600" />
            {t('nav.shoppingGuide')}
          </Link>

          <button
            type="button"
            onClick={() => setMobileCatsOpen((v) => !v)}
            className="flex items-center justify-between gap-2 text-foreground-900 text-sm font-semibold py-2.5 cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-primary-600" />
              {t('nav.categories')}
            </span>
            <i
              className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${
                mobileCatsOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {mobileCatsOpen && (
            <div className="pl-4 grid grid-cols-2 gap-1 pb-2">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileOpen(false)}
                  className="text-foreground-700 text-sm py-1.5 whitespace-nowrap cursor-pointer hover:text-primary-700 transition-colors"
                >
                  {t(`categoryNames.${cat.id}`)}
                </a>
              ))}
            </div>
          )}

          <a
            href="#faq"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 text-foreground-900 text-sm font-semibold py-2.5 cursor-pointer"
          >
            <i className="ri-question-line text-primary-600" />
            FAQ
          </a>

          <div className="flex flex-wrap gap-2 pt-2">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => changeLang(l.code)}
                className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer ${
                  currentLang === l.code ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-700'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="mt-2 px-4 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold text-center whitespace-nowrap cursor-pointer"
          >
            {t('nav.buyNow')}
          </a>
        </div>
      )}
    </header>
  );
}